import { useTranslation } from 'react-i18next';
import { cn } from 'lib/utils';
import { Input } from 'components/ui/input';
import { TIME_WINDOWS, windowOptionLabel, type TimeWindow } from 'constants/timeWindows';
import { TINT } from 'pages/MyInterventionRequests/requestVisuals';

const WINDOWS = Object.keys(TIME_WINDOWS) as TimeWindow[];

interface TimeWindowPickerProps {
  /** `null` = nothing picked yet, sent as `null` — never defaulted to `none`. */
  value: TimeWindow | null;
  indicativeTime: string;
  onChange: (window: TimeWindow) => void;
  onIndicativeTimeChange: (time: string) => void;
  /** Locked with the day window once a technician accepted (E14). */
  disabled?: boolean;
}

/** The ops' time window (E2/E10): native radios styled as pills, then the optional indicative time. */
const TimeWindowPicker = ({
  value,
  indicativeTime,
  onChange,
  onIndicativeTimeChange,
  disabled = false,
}: TimeWindowPickerProps) => {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col gap-3">
      <fieldset className="flex flex-col gap-1.5" disabled={disabled}>
        <legend className="mb-1.5 text-[13px] font-medium">{t('Preferred time window')}</legend>
        <div className="flex flex-wrap gap-2">
          {WINDOWS.map((window) => {
            const checked = value === window;
            return (
              <label
                key={window}
                className={cn(
                  'inline-flex min-h-[44px] cursor-pointer items-center rounded-md border px-3 text-[13px] focus-within:ring-2 focus-within:ring-ring',
                  disabled && 'cursor-not-allowed opacity-60',
                  checked
                    ? cn('border-sky-300 font-medium dark:border-sky-800', TINT.sky.bg, TINT.sky.fg)
                    : 'border-zinc-200 text-foreground hover:bg-zinc-50 dark:border-zinc-800 dark:hover:bg-zinc-900'
                )}
              >
                <input
                  type="radio"
                  name="assign-time-window"
                  className="sr-only"
                  value={window}
                  checked={checked}
                  onChange={() => onChange(window)}
                />
                {windowOptionLabel(t, window)}
              </label>
            );
          })}
        </div>
      </fieldset>
      <div className="flex flex-col gap-1.5">
        <label htmlFor="assign-indicative-time" className="text-[13px] font-medium">
          {t('Indicative time · optional')}
        </label>
        <Input
          id="assign-indicative-time"
          type="time"
          step={300}
          className="w-[140px]"
          aria-describedby="assign-indicative-time-hint"
          value={indicativeTime}
          disabled={disabled}
          onChange={(event) => onIndicativeTimeChange(event.target.value)}
        />
        <p id="assign-indicative-time-hint" className="text-[12px] text-muted-foreground">
          {t("Prefills the technician's time. They can change it.")}
        </p>
      </div>
    </div>
  );
};

export default TimeWindowPicker;
